// 🔧 role_context.js
// ✅ Purpose: Provide global and per-event user role context
// ✅ Features:
// - Loads global role from /users/{uid}
// - Event role is only set when loadEventRole() is explicitly called

import React, { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth, db, getUserRole } from "./firebase";
import { doc, getDoc } from "firebase/firestore";
import { logger } from './utils/logger';

const RoleContext = createContext();
export const useRole = () => useContext(RoleContext);

export const RoleProvider = ({ children }) => {
  const [role, setRole] = useState(null);
  const [eventRole, setEventRole] = useState(null);
  const [roleLoading, setRoleLoading] = useState(true);
  const [eventRoleLoading, setEventRoleLoading] = useState(false);
  const [user, setUser] = useState(null);

  // ✅ Load global role on login
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        setRoleLoading(true);
        try {
          const fetchedRole = await getUserRole(user.uid);
          setRole(fetchedRole || "voter");
        } catch (err) {
          const offline =
            err?.code === "unavailable" || err?.code === "firestore/unavailable";
          const log = offline ? logger.warn : logger.error;

          log("Failed to load the authenticated user's role:", {
            value: err,
          });

          setRole(null);

          // A browser can retain an Auth Emulator session after the emulator
          // database has been restarted. Firestore then rejects the stale
          // identity. End that session cleanly so RoleGuard can send the user
          // back through login instead of surfacing an uncaught runtime error.
          if (
            err?.code === "permission-denied" ||
            err?.code === "firestore/permission-denied"
          ) {
            try {
              await signOut(auth);
              setUser(null);
              setEventRole(null);
            } catch (signOutError) {
              logger.error("Failed to clear the invalid auth session:", {
                value: signOutError,
              });
            }
          }
        } finally {
          setRoleLoading(false);
        }
      } else {
        setUser(null);
        setRole(null);
        setEventRole(null);
        setRoleLoading(false);
      }
    });

    return unsub;
  }, []);

  // ✅ Role logging
  useEffect(() => {
    logger.log("👤 Global Role:", {
      value: role
    });
    logger.log("🎯 Event Role:", {
      value: eventRole
    });
  }, [role, eventRole]);

  // ✅ Call this manually from RoleGuard when needed
  const loadEventRole = async (eventId) => {
    if (!user || !eventId) return;
    logger.log("🎯 loadEventRole() called with eventId:", {
      value: eventId
    });
    setEventRoleLoading(true);
    try {
      const ref = doc(db, "events", eventId, "users", user.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const eventUserData = snap.data();
        // Legacy active records may omit pendingManager. An explicit true is
        // an invitation state and must not unlock manager routes.
        const roleVal =
          eventUserData.pendingManager === true &&
          eventUserData.role === "manager"
            ? null
            : eventUserData.role || "voter";
        setEventRole(roleVal);
        logger.log("✅ Event Role loaded:", {
          value: roleVal
        });
      } else {
        logger.warn("⚠️ No event role found for user in event:", {
          value: eventId
        });
        setEventRole(null);
      }
    } catch (err) {
      logger.warn("⚠️ Failed to load event role:", {
        value: err
      });
      setEventRole(null);
    }
    setEventRoleLoading(false);
  };

  return (
    <RoleContext.Provider value={{
      role,
      roleLoading,
      eventRole,
      eventRoleLoading,
      user,
      loadEventRole, // must be exposed for RoleGuard
    }}>
      {children}
    </RoleContext.Provider>
  );
};
